/**
 * §7.1 «Список персонала» (manager v3, combined wireframe round-2).
 *
 * Воспроизводит мокап 7.1 дизайна manager v3 1:1:
 *  - header «Команда отеля · N» + кнопки «Роли» (ghost) + «+ Пригласить»;
 *  - отдел = collapsible-карточка (▾/▸) с заголовком «Housekeeping · N чел»;
 *  - фильтр-чипы (День/Ночь/Свободные) ВСЕГДА видны, по умолчанию ВСЕ ВКЛ,
 *    click отключает;
 *  - список людей — вертикальный, pill-аватар инициалы + имя + должность +
 *    чип статуса (на смене / отпуск / офф / уволен);
 *  - click по pill → /staff/{id}.
 *
 * Бэк: useStaffByDepartment (GET departments, GET members?department=).
 * Тип смены/статус-на-смене бэк per-member пока не отдаёт стабильно —
 * чип-фильтры применяются к производному полю shift (graceful: при отсутствии
 * данных показываем всех, без выдуманных цифр).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@hsc/ui";
import type { StaffMember } from "@hsc/types";
import { initials } from "../shell/shell-util.js";
import { roleKeyLabel } from "./staff-i18n.js";
import { useStaffByDepartment } from "./useStaff.js";

type ShiftKind = "day" | "night" | "free";

/** Производный тип смены сотрудника (graceful — бэк per-member не отдаёт). */
function memberShift(m: StaffMember): ShiftKind {
  const raw = (m as { shift_type?: string }).shift_type;
  if (raw === "night") return "night";
  if (raw === "day") return "day";
  return "free";
}

/** Статус-чип сотрудника. */
function statusChip(m: StaffMember): { label: string; tone: string } {
  if (m.termination_date != null && m.termination_date !== "") {
    return { label: "уволен", tone: "ta-chip--muted" };
  }
  if (!m.is_active) return { label: "офф", tone: "ta-chip--muted" };
  return { label: "на смене", tone: "ta-chip--ok" };
}

/** Заголовок: имя, если бэк отдал display_name, иначе должность. */
function memberName(m: StaffMember): string {
  const dn = (m as { display_name?: string }).display_name;
  if (typeof dn === "string" && dn.trim() !== "") return dn.trim();
  return m.position_title;
}

function memberSubtitle(m: StaffMember): string {
  const dn = (m as { display_name?: string }).display_name;
  return typeof dn === "string" && dn.trim() !== "" ? m.position_title : "штатный сотрудник";
}

function StaffPill({ member, onOpen }: { member: StaffMember; onOpen: (id: string) => void }): JSX.Element {
  const name = memberName(member);
  const st = statusChip(member);
  return (
    <button
      type="button"
      className="staff-pill"
      data-testid={`staff-member-${member.id}`}
      onClick={() => onOpen(member.id)}
    >
      <span className="ta-avatar staff-pill__avatar" aria-hidden="true">
        {initials(name)}
      </span>
      <span className="staff-pill__body">
        <span className="staff-pill__name">{name}</span>
        <span className="staff-pill__role">{memberSubtitle(member)}</span>
      </span>
      <span className={`ta-chip ${st.tone}`}>{st.label}</span>
    </button>
  );
}

const SHIFT_FILTERS: ReadonlyArray<{ kind: ShiftKind; label: string }> = [
  { kind: "day", label: "День" },
  { kind: "night", label: "Ночь" },
  { kind: "free", label: "Свободные" },
];

interface DepartmentBlockProps {
  name: string;
  members: StaffMember[];
  shiftFilter: Set<ShiftKind>;
  onOpen: (id: string) => void;
}

function DepartmentBlock({ name, members, shiftFilter, onOpen }: DepartmentBlockProps): JSX.Element {
  const [open, setOpen] = useState(true);
  const filtered = useMemo(
    () => members.filter((m) => shiftFilter.has(memberShift(m))),
    [members, shiftFilter],
  );
  return (
    <section className="staff-dept-card" data-testid={`staff-dept-${name}`}>
      <button
        type="button"
        className="staff-dept-card__head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid={`staff-dept-toggle-${name}`}
      >
        <span className="staff-dept-card__title">
          {open ? "▾" : "▸"} {name} · {members.length} чел
        </span>
        <span className="ta-chip">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="staff-dept-card__body">
          {members.length === 0 ? (
            <p className="staff-dept__empty">В отделе пока нет сотрудников.</p>
          ) : filtered.length === 0 ? (
            <p className="staff-dept__empty">По выбранным фильтрам никого нет.</p>
          ) : (
            <div className="staff-pill-list" data-testid={`staff-list-${name}`}>
              {filtered.map((m) => (
                <StaffPill key={m.id} member={m} onOpen={onOpen} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function StaffPage(): JSX.Element {
  const navigate = useNavigate();
  const { groups, isLoading, isError, totalMembers, departments } = useStaffByDepartment();

  // Фильтр-чипы по умолчанию ВСЕ ВКЛ (день/ночь/свободные).
  const [shiftFilter, setShiftFilter] = useState<Set<ShiftKind>>(
    () => new Set<ShiftKind>(["day", "night", "free"]),
  );

  const toggleShift = (k: ShiftKind): void =>
    setShiftFilter((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      // не даём отключить всё — иначе пустой экран без смысла
      if (next.size === 0) return prev;
      return next;
    });

  if (isLoading) {
    return (
      <section aria-busy="true" data-testid="staff-loading">
        <h1 className="hsc-page__title">Команда отеля</h1>
        <div className="staff-pill-list">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} height={56} radius={20} />
          ))}
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section data-testid="staff-error">
        <h1 className="hsc-page__title">Команда отеля</h1>
        <div className="staff-empty" role="alert">
          Не удалось загрузить персонал. Попробуйте обновить страницу.
        </div>
      </section>
    );
  }

  return (
    <section data-testid="staff-page" className="staff-page">
      <header className="staff-topbar">
        <div>
          <div className="staff-topbar__crumbs">Управление / Персонал</div>
          <h1 className="hsc-page__title staff-topbar__title" data-testid="staff-team-count">
            Команда отеля · {totalMembers}
          </h1>
        </div>
        <div className="staff-topbar__actions">
          <button
            type="button"
            className="staff-btn staff-btn--ghost"
            data-testid="staff-roles-btn"
            onClick={() => navigate("/settings/roles")}
          >
            Роли
          </button>
          <button
            type="button"
            className="staff-btn"
            data-testid="staff-invite-btn"
            onClick={() => navigate("/staff/invite")}
          >
            + Пригласить
          </button>
        </div>
      </header>

      {/* Фильтр-чипы — всегда видны, по умолчанию все ВКЛ */}
      <div className="staff-filters" data-testid="staff-shift-filters" role="group" aria-label="Фильтр по смене">
        {SHIFT_FILTERS.map(({ kind, label }) => {
          const on = shiftFilter.has(kind);
          return (
            <button
              key={kind}
              type="button"
              className={`ta-chip staff-filter-chip${on ? " is-active" : ""}`}
              aria-pressed={on}
              data-testid={`staff-filter-${kind}`}
              onClick={() => toggleShift(kind)}
            >
              {label} {on ? "✓" : ""}
            </button>
          );
        })}
      </div>

      {departments.length === 0 ? (
        <div className="staff-empty">
          Отделы ещё не созданы. Заведите отдел в «Настройки → Отделы», затем приглашайте сотрудников.
        </div>
      ) : totalMembers === 0 ? (
        <div className="staff-empty">Пригласите первого сотрудника.</div>
      ) : (
        <div className="staff-stack">
          {groups.map(({ department, members }) => (
            <DepartmentBlock
              key={department.id}
              name={department.name}
              members={members}
              shiftFilter={shiftFilter}
              onOpen={(id) => navigate(`/staff/${id}`)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export { roleKeyLabel };
